<script lang="ts" setup>
import ProjectsDropDown, {
  type ProjectsDropDownAction,
  type ProjectsDropDownProps,
} from '@/components/common/projects/ProjectsDropDown.vue';
import { PhBuildings } from '@phosphor-icons/vue';
import { hasPermission } from 'cesg-shared';
import {
  PermissionResource,
  PermissionType,
  type SessionUser,
} from 'cesg-shared/dist/models';
import { useI18n } from 'vue-i18n';

type PDDFilter = ProjectsDropDownProps['data']['filter'];

export type SupplierInvoiceProjectChangerMode =
  | 'disabled'
  | 'readonly'
  | 'select';

export interface SupplierInvoiceProjectChangerProps {
  selectedProjectId?: string;
  mode?: SupplierInvoiceProjectChangerMode;
  placeholder?: string;
  user: SessionUser | null;
}

const { t } = useI18n();

const props = defineProps<SupplierInvoiceProjectChangerProps>();

const emit = defineEmits<{
  (e: 'update:selectedProjectId', value: string): void;
  (e: 'action', payload: ProjectsDropDownAction): void;
}>();

const onProjectAction = (event: ProjectsDropDownAction) => {
  emit('update:selectedProjectId', event.project.id);
  emit('action', event);
};

const removeIllegalProjects: PDDFilter = (project) =>
  props.user
    ? hasPermission(
        project.id,
        [
          {
            resource: PermissionResource.INVOICE,
            permission: PermissionType.EDIT,
          },
        ],
        props.user,
      )
    : false;
</script>
<template>
  <div
    class="project-picker"
    :class="{
      'is-readonly': (props.mode ?? 'select') === 'readonly',
      'is-disabled': (props.mode ?? 'select') === 'disabled',
    }"
  >
    <div class="project-picker__label-wrap">
      <div class="project-picker__label">
        <PhBuildings :size="20" weight="duotone" class="project-picker__icon" />
        <span>{{ t('invoices.details.project') }}</span>
      </div>
    </div>
    <div class="project-picker__control">
      <ProjectsDropDown
        :data="{
          selectedProjectId: props.selectedProjectId ?? undefined,
          placeholder: props.placeholder,
          includeArchived: false,
          filter: removeIllegalProjects,
        }"
        :mode="props.mode ?? 'select'"
        @action="onProjectAction"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.project-picker {
  display: grid;
  grid-template-columns: 7rem minmax(0, 1fr);
  column-gap: 0.75rem;
  align-items: center;
  margin-top: 0.45rem;
  margin-bottom: 0.35rem;
  max-width: 32rem;

  &__label-wrap {
    display: flex;
    align-items: center;
    height: 100%;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-weight: bold;
    font-size: inherit;
    white-space: nowrap;
  }

  &__icon {
    padding: 0.22rem;
    border-radius: 999px;
    border: 1px solid #cbd5e1;
    background-color: #ffffff;
    color: #64748b;
    flex-shrink: 0;
  }

  &__control {
    min-width: 0;
    display: flex;
    align-items: center;
  }
}

@media (max-width: 768px) {
  .project-picker {
    grid-template-columns: 1fr;
    row-gap: 0.35rem;
    max-width: 100%;
  }
}
</style>
